import React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { HiOutlineExternalLink, HiOutlinePencil } from 'react-icons/hi';

const STATUS_LABELS = {
  pending: 'Pending',
  solved: 'Solved',
  completed: 'Completed',
  resolved: 'Solved',
};

const STATUS_VARIANTS = {
  pending: 'pending',
  solved: 'solved',
  completed: 'completed',
  resolved: 'solved',
};

const formatStatusLabel = (status) => {
  if (!status) {
    return STATUS_LABELS.pending;
  }

  const key = typeof status === 'string' ? status.toLowerCase() : 'pending';
  return STATUS_LABELS[key] ?? STATUS_LABELS.pending;
};

const formatStatusVariant = (status) => {
  if (!status) {
    return STATUS_VARIANTS.pending;
  }

  const key = typeof status === 'string' ? status.toLowerCase() : 'pending';
  return STATUS_VARIANTS[key] ?? STATUS_VARIANTS.pending;
};

const formatDisplayDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const ComplaintsTable = ({
  rows,
  loading = false,
  error,
  page,
  pageCount,
  pageSize,
  onPageChange,
  onEdit,
  onView,
}) => {
  const currentPage =
    typeof page === 'number' && Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const normalizedPageCount =
    typeof pageCount === 'number' && Number.isFinite(pageCount) && pageCount > 0
      ? Math.ceil(pageCount)
      : 1;
  const safePageCount = Math.max(normalizedPageCount, 1);
  const computedPageSize = pageSize || (hasRows ? rows.length : 0);
  const pageOffset = computedPageSize ? (currentPage - 1) * computedPageSize : 0;
  const handlePageChange = typeof onPageChange === 'function' ? onPageChange : () => {};
  const handleEditAction = typeof onEdit === 'function' ? onEdit : () => {};
  const handleViewAction = typeof onView === 'function' ? onView : () => {};

  const handlePrev = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < safePageCount) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePageSelect = (targetPage) => {
    if (targetPage !== currentPage) {
      handlePageChange(targetPage);
    }
  };

  const renderPaginationButtons = () => {
    if (!safePageCount || safePageCount <= 1) {
      return null;
    }

    return Array.from({ length: safePageCount }, (_, index) => {
      const pageNumber = index + 1;
      const isActive = pageNumber === currentPage;

      return (
        <li key={pageNumber} className={`page-item ${isActive ? 'active' : ''}`} aria-current={isActive ? 'page' : undefined}>
          <button
            type="button"
            className="page-link"
            onClick={() => handlePageSelect(pageNumber)}
          >
            {pageNumber}
          </button>
        </li>
      );
    });
  };

  return (
    <div className="card complaints-table-card shadow-sm">
      <div className="card-body p-0">
        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : error ? (
          <div className="py-5 px-3">
            <Alert variant="danger" className="mb-0">
              {error}
            </Alert>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 complaints-table align-middle">
              <thead>
                <tr>
                  <th style={{ width: '6%' }}>S.No</th>
                  <th style={{ width: '14%' }}>Date</th>
                  <th style={{ width: '18%' }}>Name</th>
                  <th style={{ width: '16%' }}>Mobile Number</th>
                  <th>Suggestions / Complaints</th>
                  <th style={{ width: '18%' }} className="text-center">
                    Status / Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasRows ? (
                  rows.map((row, index) => {
                    const statusVariant = formatStatusVariant(row.status);
                    const statusLabel = formatStatusLabel(row.status);

                    return (
                      <tr key={row.id}>
                        <td className="fw-semibold text-center">{pageOffset + index + 1}</td>
                        <td>{formatDisplayDate(row.date || row.createdAt || row.updatedAt)}</td>
                        <td className="fw-semibold text-capitalize">{row.name}</td>
                        <td className="font-monospace">{row.mobile}</td>
                        <td>
                          {row.subject && <p className="fw-semibold mb-1">{row.subject}</p>}
                          <p className="mb-2 text-muted small" style={{ whiteSpace: 'pre-line' }} title={row.message}>
                            {row.message}
                          </p>
                          {row.resolutionNote && statusVariant !== 'pending' && (
                            <p className="mb-0 text-danger fw-semibold small">
                              Resolution: {row.resolutionNote}
                            </p>
                          )}
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                            <span className={`complaints-status-badge ${statusVariant}`}>{statusLabel}</span>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-icon"
                                onClick={() => handleEditAction(row)}
                                aria-label="Edit complaint"
                                title="Edit"
                              >
                                <HiOutlinePencil size={18} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-icon"
                                onClick={() => handleViewAction(row.id)}
                                aria-label="View complaint"
                                title="View"
                              >
                                <HiOutlineExternalLink size={18} />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      No complaints found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && (
        <div className="complaints-table-footer d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 px-4 py-3">
          <div className="text-muted small">Page {currentPage} of {safePageCount}</div>
          <nav aria-label="Suggestions / Complaints pagination">
            <ul className="pagination complaints-pagination mb-0">
              <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className="page-link"
                  onClick={handlePrev}
                  aria-label="Previous page"
                  disabled={currentPage <= 1}
                >
                  &laquo;
                </button>
              </li>
              {renderPaginationButtons()}
              <li className={`page-item ${currentPage >= safePageCount ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className="page-link"
                  onClick={handleNext}
                  aria-label="Next page"
                  disabled={currentPage >= safePageCount}
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ComplaintsTable;
